import { useEffect } from 'react';

/**
 * A hook to handle infinite scrolling using IntersectionObserver.
 * 
 * @param {Object} options
 * @param {React.RefObject} options.loadMoreRef - The ref of the element to observe at the bottom of the list.
 * @param {boolean} options.hasNextPage - Whether there are more pages to fetch.
 * @param {boolean} options.isFetchingNextPage - Whether a fetch is currently in progress.
 * @param {Function} options.fetchNextPage - The function to call to fetch the next page.
 * @param {React.RefObject} [options.rootRef] - Optional root element for the IntersectionObserver.
 * @param {string} [options.rootMargin] - Optional margin around the root.
 * @param {number|number[]} [options.threshold] - Optional threshold for the observer.
 * @param {any[]} [options.dependencyArray] - Optional dependencies to re-run the observer.
 */
export function useInfiniteScroll({
  loadMoreRef,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootRef,
  rootMargin = '200px',
  threshold = 0,
  dependencyArray = []
}) {
  useEffect(() => {
    const root = rootRef?.current || null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, ...dependencyArray]);
}
